/**
 * Ocupantes de um imóvel — quem OCUPA (mora ou usa, pois pode ser comercial), com histórico. Um
 * ocupante pode estar ligado a um contrato de aluguel (locatário e dependentes) ou ser independente
 * dele (o dono que mora, um zelador, um comodatário). Encerrar/abrir contratos finaliza os ocupantes
 * conforme as regras (no backend); aqui registra-se, encerra-se (data de saída) e remove-se um
 * ocupante avulso. Ocupante ATUAL = sem data de fim.
 */
import { BadgeCheck, Pencil, Trash2, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ComboBuscaEntidade, type ConfigBusca, type ItemBusca } from '@/components/crud/ComboBuscaEntidade';
import { DrawerAdicionar } from '@/components/crud/DrawerAdicionar';
import { Button } from '@/components/ui/button';
import { estadoDaLista } from '@/components/ui/estado';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Chip } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { formatar } from '@/lib/mascaras';

type Ocupante = {
  id: string;
  owner_tipo: string;
  owner: { tipo: string; id: string; rotulo: string } | null;
  contrato_id: string | null;
  tipo: string;
  data_inicio: string | null;
  data_fim: string | null;
  observacoes: string | null;
};
type Contrato = { id: string; situacao: string; data_inicio: string | null };

const TIPOS = [
  { valor: 'proprietario_morador', rotulo: 'Proprietário morador' },
  { valor: 'locatario', rotulo: 'Locatário' },
  { valor: 'dependente', rotulo: 'Dependente' },
  { valor: 'funcionario', rotulo: 'Funcionário' },
  { valor: 'comodatario', rotulo: 'Comodatário' },
  { valor: 'ocupante', rotulo: 'Ocupante' },
];
const rotuloTipo = (t: string) => TIPOS.find((x) => x.valor === t)?.rotulo ?? t;

const BUSCA: Record<'pessoa' | 'empresa', ConfigBusca> = {
  pessoa: {
    endpoint: '/cadastro/pessoas',
    rotulo: (p) => String(p.nome ?? ''),
    sub: (p) => (p.cpf ? formatar(String(p.cpf), 'cpf') : null),
    placeholder: 'Buscar pessoa por nome ou CPF…',
    camposRapido: [
      { chave: 'nome', rotulo: 'Nome', tipo: 'texto', obrigatorio: true },
      { chave: 'cpf', rotulo: 'CPF', tipo: 'documento', formato: 'cpf' },
    ],
  },
  empresa: {
    endpoint: '/cadastro/empresas',
    rotulo: (e) => String(e.razao_social ?? e.nome_fantasia ?? ''),
    sub: (e) => (e.cnpj ? formatar(String(e.cnpj), 'cnpj') : null),
    placeholder: 'Buscar empresa por razão social ou CNPJ…',
    camposRapido: [
      { chave: 'razao_social', rotulo: 'Razão social', tipo: 'texto', obrigatorio: true },
      { chave: 'cnpj', rotulo: 'CNPJ', tipo: 'documento', formato: 'cnpj' },
    ],
  },
};

const dataBR = (iso?: string | null) => (iso ? iso.split('-').reverse().join('/') : '');
function periodo(inicio: string | null, fim: string | null): string {
  const i = dataBR(inicio);
  const f = dataBR(fim);
  if (i && f) return `${i} → ${f}`;
  if (i) return `desde ${i}`;
  if (f) return `até ${f}`;
  return '—';
}

const CAMPO =
  'h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]';

export function PainelOcupantes({ imovelId }: { imovelId: string }) {
  const url = `/cadastro/imoveis/${imovelId}/ocupantes`;
  const [ocupantes, setOcupantes] = useState<Ocupante[]>([]);
  const [vigentes, setVigentes] = useState<Contrato[]>([]);
  const [carregando, setCarregando] = useState(true);

  const [tipoOwner, setTipoOwner] = useState<'pessoa' | 'empresa'>('pessoa');
  const [alvo, setAlvo] = useState<ItemBusca | null>(null);
  const [tipo, setTipo] = useState('ocupante');
  const [contratoId, setContratoId] = useState('');
  const [inicio, setInicio] = useState('');
  const [obs, setObs] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function carregar() {
    const [ro, rc] = await Promise.all([
      api.get<Ocupante[]>(url),
      api.get<Contrato[]>(`/cadastro/imoveis/${imovelId}/contratos`),
    ]);
    setCarregando(false);
    if (ro.ok) setOcupantes(ro.dados);
    if (rc.ok) setVigentes(rc.dados.filter((c) => c.situacao === 'vigente'));
  }

  useEffect(() => {
    setCarregando(true);
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  function limparForm() {
    setAlvo(null);
    setTipo('ocupante');
    setContratoId('');
    setInicio('');
    setObs('');
    setTipoOwner('pessoa');
  }

  async function adicionar(): Promise<boolean> {
    if (!alvo || salvando) return false;
    setSalvando(true);
    const r = await api.post(url, {
      owner_tipo: tipoOwner,
      owner_id: alvo.id,
      tipo,
      contrato_id: contratoId || null,
      data_inicio: inicio || null,
      observacoes: obs || null,
    });
    setSalvando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return false;
    }
    toast.success(`${BUSCA[tipoOwner].rotulo(alvo)} registrado como ${rotuloTipo(tipo)}.`);
    limparForm();
    void carregar();
    return true;
  }

  async function encerrar(o: Ocupante) {
    const hoje = new Date().toISOString().slice(0, 10);
    const r = await api.patch(`/cadastro/imoveis/ocupantes/${o.id}`, { data_fim: hoje });
    if (!r.ok) return void toast.error(r.erro);
    toast.success('Ocupação encerrada.');
    void carregar();
  }

  async function remover(o: Ocupante) {
    const r = await api.del(`/cadastro/imoveis/ocupantes/${o.id}`);
    if (!r.ok) return void toast.error(r.erro);
    toast.success('Ocupante removido.');
    void carregar();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="size-4 text-accent" />
          Ocupantes
        </CardTitle>
        <p className="text-[13px] leading-[1.5] text-text-secondary">
          Quem ocupa o imóvel (residencial ou comercial), com histórico. Podem estar ligados a um
          contrato de aluguel ou ser independentes dele. Ocupante atual não tem data de saída.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {estadoDaLista(carregando, ocupantes.length === 0, 'Nenhum ocupante ainda.') ?? (
          <div className="flex flex-col gap-2">
            {ocupantes.map((o) => (
              <div
                key={o.id}
                className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
              >
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-[15px] text-text">{o.owner?.rotulo ?? '—'}</span>
                  <span className="tipo-corpo-pequeno text-text-tertiary">
                    {o.owner?.tipo === 'empresa' ? 'Empresa' : 'Pessoa'} · {periodo(o.data_inicio, o.data_fim)}
                    {o.contrato_id ? ' · ligado a contrato' : ''}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {!o.data_fim && (
                    <Chip aceso>
                      <BadgeCheck className="size-3.5" />
                      Atual
                    </Chip>
                  )}
                  <Chip>{rotuloTipo(o.tipo)}</Chip>
                  {!o.data_fim && (
                    <Button variant="fantasma" size="sm" onClick={() => void encerrar(o)} aria-label="Encerrar ocupação">
                      <Pencil />
                    </Button>
                  )}
                  <Button
                    variant="fantasma"
                    size="sm"
                    className="text-danger hover:text-danger"
                    onClick={() => void remover(o)}
                    aria-label="Remover"
                  >
                    <Trash2 />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <DrawerAdicionar
          titulo="Novo ocupante"
          descricao="Busque a pessoa/empresa, o tipo de ocupação e (se houver) o contrato de aluguel que a vincula."
          rotuloAbrir="Adicionar ocupante"
          rotuloSalvar="Registrar"
          podeSalvar={!!alvo}
          salvando={salvando}
          onSalvar={adicionar}
          onFechar={limparForm}
        >
          <div className="flex flex-col gap-2">
            <span className="tipo-rotulo">Quem</span>
            <div className="flex gap-1 rounded-md bg-surface-2 p-1">
              {(['pessoa', 'empresa'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    setTipoOwner(t);
                    setAlvo(null);
                  }}
                  className={`flex-1 rounded-sm px-3 py-1.5 text-[13px] font-medium capitalize transition ${
                    tipoOwner === t ? 'bg-surface text-text shadow-[var(--shadow-sm)]' : 'text-text-tertiary'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            <ComboBuscaEntidade key={tipoOwner} config={BUSCA[tipoOwner]} onSelecionar={setAlvo} />
          </div>
          <label className="flex flex-col gap-2">
            <span className="tipo-rotulo">Tipo de ocupação</span>
            <select value={tipo} onChange={(e) => setTipo(e.target.value)} className={CAMPO}>
              {TIPOS.map((t) => (
                <option key={t.valor} value={t.valor}>
                  {t.rotulo}
                </option>
              ))}
            </select>
          </label>
          {vigentes.length > 0 && (
            <label className="flex flex-col gap-2">
              <span className="tipo-rotulo">Contrato de aluguel (opcional)</span>
              <select value={contratoId} onChange={(e) => setContratoId(e.target.value)} className={CAMPO}>
                <option value="">Sem contrato (independente)</option>
                {vigentes.map((c) => (
                  <option key={c.id} value={c.id}>
                    Contrato vigente desde {dataBR(c.data_inicio) || '—'}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="flex flex-col gap-2">
            <span className="tipo-rotulo">Entrada / início</span>
            <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} className={CAMPO} />
          </label>
          <label className="flex flex-col gap-2">
            <span className="tipo-rotulo">Observações</span>
            <textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={2} className={`${CAMPO} h-auto py-2`} />
          </label>
        </DrawerAdicionar>
      </CardContent>
    </Card>
  );
}
